import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  DashboardOutlined,
  UserOutlined,
  DesktopOutlined,
  HddOutlined,
} from "@ant-design/icons";
import { Button, ConfigProvider, Layout, Menu, Select, message } from "antd";
import { Header } from "antd/es/layout/layout";
import { jwtDecode } from "jwt-decode";

import useAuthenthication from "./hooks/useAuthentication";
import { useSelector } from "react-redux";
import { IUser } from "./models/user";
import { ItemType } from "antd/es/menu/hooks/useItems";
import {
  UserRole,
  canAccessDevices,
  canManageUsers,
  hasFullMenu,
  isUserRole,
  resolveRole,
} from "./services/roles";
import { useI18n } from "./i18n/I18nContext";
import { Locale } from "./i18n/messages";

const { Sider, Content } = Layout;

const AppPage: React.FC = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { t, locale, setLocale } = useI18n();

  const { logoutCall } = useAuthenthication();
  const user = useSelector(({ UserSlice }: any) => UserSlice.user as IUser);
  const accessToken = useSelector(
    ({ UserSlice }: any) => UserSlice.accessToken as string | null
  );

  const role: UserRole = useMemo(() => {
    if (user?.role) {
      return resolveRole(user.role);
    }
    if (accessToken) {
      try {
        const decoded: any = jwtDecode(accessToken);
        return resolveRole(decoded?.role);
      } catch {
        /* ignore */
      }
    }
    return "admin";
  }, [user?.role, accessToken]);

  const navigation = useMemo(() => {
    if (hasFullMenu(role)) {
      return [
        {
          label: "Показатели",
          key: "/main",
          icon: <DashboardOutlined size={24} />,
        },
        {
          label: t("app.devices"),
          key: "/devices",
          icon: <HddOutlined size={24} />,
        },
        {
          label: "AJAX",
          key: "ajax",
          icon: <DashboardOutlined />,
          children: [
            {
              label: "Пользователи",
              key: "/ajax-users",
              icon: <UserOutlined size={24} />,
            },
          ],
        },
        {
          label: "Клиенты",
          key: "/clients",
          icon: <UserOutlined size={24} />,
        },
        {
          label: t("app.users"),
          key: "/operators",
          icon: <DesktopOutlined size={24} />,
        },
      ] as ItemType[];
    }

    const items: ItemType[] = [];

    if (canAccessDevices(role)) {
      items.push({
        label: t("app.devices"),
        key: "/devices",
        icon: <HddOutlined size={24} />,
      });
    }

    // FIX-001: Пользователи у manager, нет у operator
    if (canManageUsers(role)) {
      items.push({
        label: t("app.users"),
        key: "/operators",
        icon: <DesktopOutlined size={24} />,
      });
    }

    return items;
  }, [role, t]);

  const handleMenuClick = ({ key }: any) => {
    if (key) {
      navigate(key);
    }
  };

  const signOutUser = async () => await logoutCall();

  useEffect(() => {
    document.title = "SP API";
    // Ошибки держатся дольше — успевают прочитать
    message.config({ duration: 8, maxCount: 3 });
  }, []);

  useEffect(() => {
    if (isUserRole(role)) {
      const path = window.location.pathname;
      const allowed =
        path.startsWith("/devices") ||
        (role === "manager" && path.startsWith("/operators")) ||
        path === "/sign-in";
      if (!allowed) {
        navigate("/devices");
      }
    }
  }, [role, navigate]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#27C77A",
        },
      }}
    >
      <Layout style={{ height: "100vh" }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          style={{ height: "100%" }}
        >
          <div
            style={{
              height: 32,
              margin: 16,
              background: "rgba(255, 255, 255, 0.2)",
            }}
            className="LOGOTYPE"
          />
          <Menu
            theme="dark"
            defaultSelectedKeys={
              isUserRole(role) ? ["/devices"] : ["/main"]
            }
            mode="inline"
            items={navigation}
            onClick={handleMenuClick}
          />
        </Sider>

        <Layout className="site-layout">
          <Header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              background: "#2c2c2c",
              gap: 12,
            }}
          >
            {isUserRole(role) && (
              <Select
                value={locale}
                style={{ width: 130 }}
                onChange={(v: Locale) => setLocale(v)}
                options={[
                  { value: "ru", label: t("lang.ru") },
                  { value: "ro", label: t("lang.ro") },
                ]}
              />
            )}
            <Button onClick={signOutUser}>
              {t("app.logout")} - {user?.email || user?.username}
              {role ? ` (${role})` : ""}
            </Button>
          </Header>
          <Content style={{ margin: "16px" }}>
            <div
              style={{
                padding: 24,
                height: "100vh",
                background: "#fff",
                overflow: "auto",
              }}
            >
              <Outlet />
            </div>
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export default AppPage;
